import classnames from "classnames";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Button, Col, Container, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";

import type { Invoice } from "../../../../api";
import KpiTile from "../../../../components/admin/KpiTile";
import PageHeader from "../../../../components/admin/PageHeader";
import useManualPaymentHistory from "../../../../components/manualPayments/hooks/useManualPaymentHistory";
import useManualPaymentInvoices from "../../../../components/manualPayments/hooks/useManualPaymentInvoices";
import ManualPaymentInvoicesTable from "../../../../components/manualPayments/ManualPaymentInvoicesTable";
import RecordPaymentModal from "../../../../components/manualPayments/RecordPaymentModal";

const ManualPayments: React.FC = () => {
  const { t } = useTranslation("manualPayments", { keyPrefix: "page" });
  const [activeTab, setActiveTab] = useState("pending");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from the admin overview: ?filter=overdue shows only the invoices
  // past their due date, which is what that panel counted.
  const showOverdueOnly = searchParams.get("filter") === "overdue";
  const clearOverdueFilter = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("filter");
        return next;
      },
      { replace: true }
    );

  // Pending invoices
  const {
    invoices: pendingInvoices,
    isLoading: isLoadingPending,
    refresh: refreshPending,
    ...pendingProps
  } = useManualPaymentInvoices({
    pagination: { page: 1, pageSize: 10 },
    sort: showOverdueOnly ? { dueDate: "up" } : { createdAt: "down" },
    // `dueBefore`, not `filters`: this endpoint does not read the generic
    // filters object, so a `filters.dueDate` clause was silently discarded and
    // the "past due only" view showed every pending invoice.
    extraParams: showOverdueOnly ? { dueBefore: moment().startOf("day").toISOString() } : undefined,
  });

  // Payment history
  const {
    invoices: historyInvoices,
    isLoading: isLoadingHistory,
    refresh: refreshHistory,
    ...historyProps
  } = useManualPaymentHistory({
    pagination: { page: 1, pageSize: 10 },
    sort: { manualPaymentRecordedAt: "down" },
  });

  // Calculate totals
  const totalPending = pendingProps?.pagination?.total || 0;
  const totalPendingAmount = pendingInvoices.reduce((sum, inv) => sum + (inv.netPayable || 0), 0);

  const handleRecordPayment = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsRecordModalOpen(true);
  };

  const handlePaymentRecorded = () => {
    refreshPending();
    refreshHistory();
  };

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <React.Fragment>
      <RecordPaymentModal
        isOpen={isRecordModalOpen}
        toggle={() => setIsRecordModalOpen(false)}
        invoice={selectedInvoice}
        onPaymentRecorded={handlePaymentRecorded}
      />

      <Container className="mt-4" fluid>
        <PageHeader
          title={t("pendingTitle")}
          subtitle={showOverdueOnly ? t("overdueSubtitle") : t("pendingDescription")}
        >
          {showOverdueOnly && (
            <Button color="warning" size="sm" outline onClick={clearOverdueFilter}>
              <i className="fas fa-filter mr-1" aria-hidden="true" />
              {t("filters.overdue")}
              <i className="fas fa-times ml-2" aria-hidden="true" />
            </Button>
          )}
        </PageHeader>
        <Row className="mb-3" style={{ rowGap: 12 }}>
          <Col xs={12} sm={6} xl={3}>
            <KpiTile
              label={t("pendingInvoices")}
              value={totalPending}
              tone={totalPending > 0 ? "warn" : "ok"}
              detail={t("pendingDescription")}
              icon="fas fa-file-invoice"
              isLoading={isLoadingPending && !totalPending}
            />
          </Col>
          <Col xs={12} sm={6} xl={3}>
            <KpiTile
              label={t("pendingAmount")}
              value={`${(Math.round(totalPendingAmount * 100) / 100).toFixed(2)} €`}
              detail={t("pendingAmountDescription")}
              icon="fas fa-euro-sign"
              isLoading={isLoadingPending && !totalPending}
            />
          </Col>
        </Row>

        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "pending" })}
              onClick={() => toggleTab("pending")}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-clock mr-2" />
              {t("tabs.pending")} {totalPending > 0 && `(${totalPending})`}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === "history" })}
              onClick={() => toggleTab("history")}
              style={{ cursor: "pointer" }}
            >
              <i className="fas fa-history mr-2" />
              {t("tabs.history")}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          <TabPane tabId="pending">
            <Row>
              <Col>
                <ManualPaymentInvoicesTable
                  {...pendingProps}
                  title={t("pendingTitle")}
                  invoices={pendingInvoices}
                  isLoading={isLoadingPending}
                  onRecordPayment={handleRecordPayment}
                  showHistory={false}
                />
              </Col>
            </Row>
          </TabPane>
          <TabPane tabId="history">
            <Row>
              <Col>
                <ManualPaymentInvoicesTable
                  {...historyProps}
                  title={t("historyTitle")}
                  invoices={historyInvoices}
                  isLoading={isLoadingHistory}
                  showHistory={true}
                />
              </Col>
            </Row>
          </TabPane>
        </TabContent>
      </Container>
    </React.Fragment>
  );
};

export default ManualPayments;
